const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

/* ---------------------------------------------------------------------------
 * Shazory Variant Price Safeguard v2 (Teeinblue-aware)
 *
 * Diagnosis confirmed: this store uses Teeinblue, a customizer/personalization
 * app that replaces Dawn's standard variant picker with its own UI. Teeinblue
 * does NOT reliably dispatch change events on Dawn's hidden cart input when
 * the customer picks a size — so Dawn's price-update chain never fires.
 *
 * Strategy: don't depend on Dawn's structure. Watch the URL's ?variant=
 * parameter (Teeinblue updates this when the customer picks a variant) and
 * apply the corresponding price directly to the DOM. Fast, no section fetch,
 * works regardless of how the picker is rendered.
 *
 * Layers (all run concurrently):
 *   1. Monkey-patched history.pushState / history.replaceState
 *   2. popstate event
 *   3. 350ms URL polling for 15 minutes
 *   4. Document-wide click delegation, re-checks URL ~150ms after any click
 *
 * Price source: /products/{handle}.js  — fetched once at startup, cached.
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';

  if (window.__shazoryPriceSafeguardInit) return;

  // Only run on product pages
  if (!/\/products\//.test(location.pathname)) return;

  window.__shazoryPriceSafeguardInit = true;

  var variantsById = {};
  var lastSeenVariant = null;
  var moneyFormat = null;

  function getVariantFromUrl() {
    var m = location.search.match(/[?&]variant=([^&]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  function getHandle() {
    var m = location.pathname.match(/\/products\/([^\/\?#]+)/);
    return m ? m[1] : null;
  }

  // Shopify-style money formatter. Falls back to "$X.XX USD" if no format set.
  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    var amount = (cents / 100).toFixed(2);
    if (moneyFormat && moneyFormat.indexOf('{{') !== -1) {
      try {
        // very basic {{amount}} substitution
        return moneyFormat
          .replace(/\{\{\s*amount\s*\}\}/g, amount)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(parseFloat(amount))))
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','));
      } catch (e) { /* fall through */ }
    }
    return '$' + amount + ' USD';
  }

  function applyPrice(variant) {
    if (!variant) return;
    var priceText = formatMoney(variant.price);

    // 1) Dawn-style price blocks: #price-{section} ... .price-item--regular/--last/--sale
    document.querySelectorAll('[id^="price-"]').forEach(function (block) {
      block.querySelectorAll(
        '.price-item--regular, .price-item--last, .price-item--sale'
      ).forEach(function (el) {
        el.textContent = priceText;
      });
    });

    // 2) Generic fallbacks for any other theme's price markup that may be present
    document.querySelectorAll(
      '.product__price .money, .product-price .money, [data-product-price]'
    ).forEach(function (el) {
      el.textContent = priceText;
    });

    // 3) Update hidden cart inputs so add-to-cart uses the right variant
    document.querySelectorAll('input[name="id"]').forEach(function (input) {
      input.value = String(variant.id);
      try { input.setAttribute('value', String(variant.id)); } catch (e) {}
    });

    // 4) Strikethrough compare-at, if applicable
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      var compareText = formatMoney(variant.compare_at_price);
      document.querySelectorAll('s.price-item--regular, .price__sale s').forEach(function (el) {
        el.textContent = compareText;
      });
    }
  }

  function checkUrl() {
    var current = getVariantFromUrl();
    if (!current || current === lastSeenVariant) return;
    if (!variantsById[current]) return;
    lastSeenVariant = current;
    applyPrice(variantsById[current]);
  }

  function loadProduct() {
    var handle = getHandle();
    if (!handle) return Promise.reject();
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        (data.variants || []).forEach(function (v) {
          variantsById[String(v.id)] = v;
        });
        // Capture money format from Shopify if available
        if (window.Shopify && window.Shopify.money_format) {
          moneyFormat = window.Shopify.money_format;
        }
        return data;
      });
  }

  function start() {
    lastSeenVariant = null; // force first apply

    loadProduct().then(function () {
      // First-pass correction in case Teeinblue rendered with the wrong default
      var current = getVariantFromUrl();
      if (current && variantsById[current]) {
        lastSeenVariant = current;
        applyPrice(variantsById[current]);
      }

      // Layer 1: history.pushState / replaceState patches
      try {
        var origReplace = history.replaceState;
        var origPush = history.pushState;
        history.replaceState = function () {
          origReplace.apply(this, arguments);
          setTimeout(checkUrl, 0);
        };
        history.pushState = function () {
          origPush.apply(this, arguments);
          setTimeout(checkUrl, 0);
        };
      } catch (e) { /* ignore */ }

      // Layer 2: popstate
      window.addEventListener('popstate', checkUrl);

      // Layer 3: URL polling (caps after 15 minutes per page)
      var poll = setInterval(checkUrl, 350);
      setTimeout(function () { clearInterval(poll); }, 15 * 60 * 1000);

      // Layer 4: document-wide click delegation, re-check URL after click
      document.addEventListener('click', function () {
        setTimeout(checkUrl, 100);
        setTimeout(checkUrl, 300);
      }, true);
    }).catch(function () {
      // If product JSON failed, do nothing — Dawn's chain remains untouched.
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
